import { FC, useState, useEffect } from 'react';
import { flow } from 'fp-ts/function';
import isEmpty from 'lodash/isEmpty';
import {
  InternalTemplates,
  PredictiveGoalQuery,
  UserSelectedInternalTemplate,
} from 'modules/predictiveGoals/types';
import {
  PredictiveGoalsContext,
  defaultPredictiveGoalsState,
  PredictiveGoalContextState,
} from './PredictiveGoalsContext';
import { useCoreGoalsContext } from '../CoreGoalsContext/CoreGoalsContext';
import { createTemplateGoalQueriesfromTemplateTypes } from './contextUtils';

export const PredictiveGoalsProvider: FC<React.PropsWithChildren<unknown>> = ({ children }) => {
  const {
    vocab: { vocabData },
    areAllReady,
  } = useCoreGoalsContext();
  const [templateSelection, setTemplateSelection] = useState<UserSelectedInternalTemplate>(
    defaultPredictiveGoalsState.userTemplateSelection,
  );
  const [parsedGoal, setParsedGoal] = useState<PredictiveGoalQuery>({
    ...defaultPredictiveGoalsState.queriedGoal,
  });
  const [prgEnhancementsTemplateQueries, setPrgEnhancementsTemplateQueries] = useState<
    Record<InternalTemplates, PredictiveGoalQuery>
  >(defaultPredictiveGoalsState.internalTemplateGoalQueries);

  // Respond to user template selection
  const onSetUserTemplate = (templateType: UserSelectedInternalTemplate) => {
    setTemplateSelection(templateType);
  };

  useEffect(() => {
    if (areAllReady) {
      flow(
        createTemplateGoalQueriesfromTemplateTypes,
        setPrgEnhancementsTemplateQueries,
      )(vocabData);
    }
  }, [vocabData, areAllReady]);

  useEffect(() => {
    if (templateSelection) {
      setParsedGoal(prgEnhancementsTemplateQueries[templateSelection]);
    }
  }, [templateSelection, prgEnhancementsTemplateQueries]);

  const providerValue: PredictiveGoalContextState = {
    ...defaultPredictiveGoalsState,
    userTemplateSelection: templateSelection,
    setUserTemplateSelection: onSetUserTemplate,
    queriedGoal: parsedGoal,
    resetQueriedGoal: () => {
      setParsedGoal(defaultPredictiveGoalsState.queriedGoal);
    },
    internalTemplateGoalQueries: prgEnhancementsTemplateQueries,
    isTemplateEventDisabled: (internalEvent: UserSelectedInternalTemplate) => {
      if (internalEvent === '') {
        return true;
      }
      const eventCriteria = prgEnhancementsTemplateQueries[internalEvent];
      return isEmpty(eventCriteria.and_clauses);
    },
  };

  return (
    <PredictiveGoalsContext.Provider value={providerValue}>
      {children}
    </PredictiveGoalsContext.Provider>
  );
};
